import {series} from 'async';
import React, {ReactElement, useCallback, useRef, useState} from 'react';
import {OverlayTrigger} from 'shared/components/OverlayTrigger';
import Icon from 'shared/Icon';
import styled from 'styled-components';

interface ClickToCopyProps {
    title?: string;
    children: string | ReactElement | ReactElement[];
}

export const ClickToCopy = ({children, title}: ClickToCopyProps) => {
    const reference = useRef<HTMLSpanElement>(null);
    const [copyText, setCopyText] = useState(`Click to copy ${title}`);
    const [show, setShow] = useState(false);

    const clickHandler = useCallback(() => {
        const value = reference.current.innerHTML;

        navigator.clipboard
            .writeText(value)
            .then(() => {
                const tempText = copyText;
                const timeouts: NodeJS.Timeout[] = [];
                series(
                    [
                        (callback) => {
                            setShow(false);
                            callback(null);
                        },
                        (callback) => {
                            timeouts.push(
                                setTimeout(() => {
                                    setCopyText('Copied!');
                                    setShow(true);
                                    callback(null);
                                }, 200)
                            );
                        },
                        (callback) => {
                            timeouts.push(
                                setTimeout(() => {
                                    setShow(false);
                                    callback(null);
                                }, 1000)
                            );
                        },
                        (callback) => {
                            timeouts.push(
                                setTimeout(() => {
                                    setCopyText(tempText);
                                    callback(null);
                                }, 100)
                            );
                        },
                    ],
                    () => {
                        setShow(false);
                        timeouts.forEach((timeout) => {
                            clearTimeout(timeout);
                        });
                    }
                );
            })
            .catch((err) => {
                // eslint-disable-next-line no-console -- just an error log
                console.error('Failed to copy: ', err);
            });
    }, [copyText]);

    return (
        <OverlayTrigger
            overlay={copyText}
            placement={'top'}
            controlledShow={show}>
            <Container onClick={clickHandler}>
                <span ref={reference}>{children}</span>
                <Icon iconName="Options-Copy-white.svg" />
            </Container>
        </OverlayTrigger>
    );
};

const Container = styled.span`
    cursor: pointer;

    display: flex;
    justify-content: space-between;
    align-items: center;

    > img {
        width: 20px;
        margin-left: 15px;
    }
`;
